// src/components/TaskDetailDrawer.jsx
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  X, Phone, Mail, MessageCircle, Calendar, FileText, ClipboardList, Check,
  Clock, Trash2, ChevronDown, User, AlertCircle, Edit2, Save
} from "lucide-react";

const BASE = process.env.REACT_APP_API_URL || "";

// Task type configurations
const TASK_TYPES = {
  call: { icon: Phone, label: 'Phone Call', color: 'bg-green-100 text-green-600', action: 'call' },
  email: { icon: Mail, label: 'Send Email', color: 'bg-purple-100 text-purple-600', action: 'email' },
  sms: { icon: MessageCircle, label: 'Send SMS', color: 'bg-blue-100 text-blue-600', action: 'sms' },
  follow_up: { icon: ClipboardList, label: 'Follow Up', color: 'bg-orange-100 text-orange-600', action: null },
  meeting: { icon: Calendar, label: 'Meeting', color: 'bg-pink-100 text-pink-600', action: null },
  document: { icon: FileText, label: 'Document', color: 'bg-yellow-100 text-yellow-600', action: null },
  general: { icon: ClipboardList, label: 'General', color: 'bg-slate-100 text-slate-600', action: null }
};

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'bg-slate-100 text-slate-600' },
  { value: 'normal', label: 'Normal', color: 'bg-blue-100 text-blue-600' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-600' },
  { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-600' }
];

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' }
];

export default function TaskDetailDrawer({ taskId, onClose, onUpdate, onDelete, onAction }) {
  const { t } = useTranslation();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedTask, setEditedTask] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRescheduleMenu, setShowRescheduleMenu] = useState(false);

  // Fetch task details
  useEffect(() => {
    if (!taskId) return;

    async function fetchTask() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${BASE}/tasks/single/${taskId}`, {
          credentials: 'include'
        });
        const data = await res.json();
        if (data.ok) {
          setTask(data.data);
          setEditedTask(data.data);
        } else {
          setError(data.error || 'Failed to load task');
        }
      } catch (err) {
        setError('Failed to load task');
      } finally {
        setLoading(false);
      }
    }

    fetchTask();
  }, [taskId]);

  // Quick complete action
  async function handleComplete() {
    try {
      const res = await fetch(`${BASE}/tasks/${taskId}/complete`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json();
      if (data.ok) {
        setTask(data.data);
        if (onUpdate) onUpdate(data.data);
      }
    } catch (err) {
      console.error('Failed to complete task:', err);
    }
  }

  // Quick reschedule action
  async function handleReschedule(when) {
    setShowRescheduleMenu(false);
    try {
      const res = await fetch(`${BASE}/tasks/${taskId}/reschedule`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ when })
      });
      const data = await res.json();
      if (data.ok) {
        setTask(data.data);
        setEditedTask(data.data);
        if (onUpdate) onUpdate(data.data);
      }
    } catch (err) {
      console.error('Failed to reschedule task:', err);
    }
  }

  // Save edited task
  async function handleSave() {
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/tasks/any/${taskId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editedTask.title,
          description: editedTask.description,
          status: editedTask.status,
          priority: editedTask.priority,
          taskType: editedTask.taskType,
          dueDate: editedTask.dueDate
        })
      });
      const data = await res.json();
      if (data.ok) {
        setTask(data.data);
        setIsEditing(false);
        if (onUpdate) onUpdate(data.data);
      }
    } catch (err) {
      console.error('Failed to save task:', err);
    } finally {
      setSaving(false);
    }
  }

  // Delete task
  async function handleDelete() {
    try {
      const res = await fetch(`${BASE}/tasks/any/${taskId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await res.json();
      if (data.ok) {
        if (onDelete) onDelete(taskId);
        onClose();
      }
    } catch (err) {
      console.error('Failed to delete task:', err);
    }
  }

  // Handle action button (call/email/sms)
  function handleAction() {
    if (!task) return;
    const typeConfig = TASK_TYPES[task.taskType] || TASK_TYPES.general;
    if (typeConfig.action && onAction) {
      onAction(typeConfig.action, task);
    }
  }

  const typeConfig = task ? (TASK_TYPES[task.taskType] || TASK_TYPES.general) : TASK_TYPES.general;
  const TypeIcon = typeConfig.icon;
  const isCompleted = task?.status === 'completed' || task?.status === 'done';

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full md:w-[520px] bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-slate-50">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${typeConfig.color}`}>
              <TypeIcon size={20} />
            </div>
            <div>
              <div className="text-lg font-bold text-slate-900">{t('taskDetails')}</div>
              <div className="text-sm text-slate-500">{typeConfig.label}</div>
            </div>
          </div>
          <button
            className="text-slate-500 hover:text-slate-700 p-2 hover:bg-slate-100 rounded-lg transition-colors"
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-slate-200 border-t-[#007cf0] rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="flex-1 flex items-center justify-center p-4">
            <div className="text-center">
              <AlertCircle className="mx-auto text-red-500 mb-2" size={32} />
              <div className="text-red-600">{error}</div>
            </div>
          </div>
        ) : task ? (
          <>
            <div className="flex-1 overflow-auto p-4 space-y-4">
              {/* Quick Actions Bar */}
              {!isEditing && !isCompleted && (
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={handleComplete}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm"
                  >
                    <Check size={16} />
                    {t('markComplete')}
                  </button>

                  <div className="relative">
                    <button
                      onClick={() => setShowRescheduleMenu(!showRescheduleMenu)}
                      className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors font-medium text-sm"
                    >
                      <Clock size={16} />
                      {t('reschedule')}
                      <ChevronDown size={14} />
                    </button>
                    {showRescheduleMenu && (
                      <div className="absolute top-full left-0 mt-1 bg-white border rounded-lg shadow-lg py-1 z-10 min-w-[140px]">
                        <button
                          onClick={() => handleReschedule('later_today')}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50"
                        >
                          {t('laterToday')}
                        </button>
                        <button
                          onClick={() => handleReschedule('tomorrow')}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50"
                        >
                          {t('tomorrow')}
                        </button>
                        <button
                          onClick={() => handleReschedule('next_week')}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50"
                        >
                          {t('nextWeek')}
                        </button>
                      </div>
                    )}
                  </div>

                  {typeConfig.action && (
                    <button
                      onClick={handleAction}
                      className="flex items-center gap-2 px-4 py-2 bg-[#007cf0] text-white rounded-lg hover:bg-[#0066cc] transition-colors font-medium text-sm"
                    >
                      <TypeIcon size={16} />
                      {typeConfig.action === 'call' ? t('makeCall') :
                       typeConfig.action === 'email' ? t('sendEmail') :
                       typeConfig.action === 'sms' ? t('sendSMS') : t('startAction')}
                    </button>
                  )}

                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors font-medium text-sm ml-auto"
                  >
                    <Edit2 size={16} />
                    {t('edit')}
                  </button>
                </div>
              )}

              {/* Status Banner for Completed */}
              {isCompleted && (
                <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Check className="text-green-600" size={20} />
                  </div>
                  <div>
                    <div className="font-semibold text-green-800">{t('taskCompleted')}</div>
                    <div className="text-sm text-green-600">
                      {task.completedAt ? new Date(task.completedAt).toLocaleString() : ''}
                    </div>
                  </div>
                </div>
              )}

              {/* Task Title */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-slate-500 uppercase">{t('taskTitle')}</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editedTask.title || ''}
                    onChange={(e) => setEditedTask({ ...editedTask, title: e.target.value })}
                    className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#007cf0] focus:border-transparent text-lg font-semibold"
                  />
                ) : (
                  <div className="text-xl font-semibold text-slate-900">{task.title}</div>
                )}
              </div>

              {/* Client Info */}
              {task.clientName && (
                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                  <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                    <User className="text-slate-500" size={18} />
                  </div>
                  <div>
                    <div className="font-medium text-slate-900">{task.clientName}</div>
                    <div className="text-sm text-slate-500 space-x-3">
                      {task.clientPhone && <span>{task.clientPhone}</span>}
                      {task.clientEmail && <span>{task.clientEmail}</span>}
                    </div>
                  </div>
                </div>
              )}

              {/* Task Type */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-slate-500 uppercase">{t('taskType')}</label>
                {isEditing ? (
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(TASK_TYPES).map(([key, config]) => {
                      const Icon = config.icon;
                      return (
                        <button
                          key={key}
                          onClick={() => setEditedTask({ ...editedTask, taskType: key })}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-colors ${
                            editedTask.taskType === key
                              ? 'border-[#007cf0] bg-blue-50'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <Icon size={16} />
                          <span className="text-sm">{config.label}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg ${typeConfig.color}`}>
                    <TypeIcon size={16} />
                    <span className="text-sm font-medium">{typeConfig.label}</span>
                  </div>
                )}
              </div>

              {/* Priority & Status Row */}
              <div className="grid grid-cols-2 gap-4">
                {/* Priority */}
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-slate-500 uppercase">{t('priority')}</label>
                  {isEditing ? (
                    <select
                      value={editedTask.priority || 'normal'}
                      onChange={(e) => setEditedTask({ ...editedTask, priority: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#007cf0]"
                    >
                      {PRIORITY_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  ) : (
                    <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
                      PRIORITY_OPTIONS.find(p => p.value === task.priority)?.color || 'bg-slate-100'
                    }`}>
                      {PRIORITY_OPTIONS.find(p => p.value === task.priority)?.label || task.priority}
                    </span>
                  )}
                </div>

                {/* Status */}
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-slate-500 uppercase">{t('status')}</label>
                  {isEditing ? (
                    <select
                      value={editedTask.status || 'pending'}
                      onChange={(e) => setEditedTask({ ...editedTask, status: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#007cf0]"
                    >
                      {STATUS_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  ) : (
                    <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
                      task.status === 'completed' ? 'bg-green-100 text-green-700' :
                      task.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {STATUS_OPTIONS.find(s => s.value === task.status)?.label || task.status}
                    </span>
                  )}
                </div>
              </div>

              {/* Due Date */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-slate-500 uppercase">{t('dueDate')}</label>
                {isEditing ? (
                  <input
                    type="datetime-local"
                    value={editedTask.dueDate ? new Date(editedTask.dueDate).toISOString().slice(0, 16) : ''}
                    onChange={(e) => setEditedTask({ ...editedTask, dueDate: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#007cf0]"
                  />
                ) : (
                  <div className="flex items-center gap-2 text-slate-700">
                    <Calendar size={16} className="text-slate-400" />
                    {task.dueDate ? new Date(task.dueDate).toLocaleString() : t('noDueDate')}
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-slate-500 uppercase">{t('description')}</label>
                {isEditing ? (
                  <textarea
                    value={editedTask.description || ''}
                    onChange={(e) => setEditedTask({ ...editedTask, description: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#007cf0] resize-none"
                    placeholder={t('addDescription')}
                  />
                ) : (
                  <div className="text-slate-700 whitespace-pre-wrap bg-slate-50 p-4 rounded-xl min-h-[80px]">
                    {task.description || <span className="text-slate-400 italic">{t('noDescription')}</span>}
                  </div>
                )}
              </div>

              {/* Timestamps */}
              <div className="border-t pt-4 space-y-2 text-sm text-slate-500">
                <div className="flex justify-between">
                  <span>{t('created')}</span>
                  <span>{task.createdAt ? new Date(task.createdAt).toLocaleString() : '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('lastUpdated')}</span>
                  <span>{task.updatedAt ? new Date(task.updatedAt).toLocaleString() : '-'}</span>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="border-t p-4 bg-slate-50">
              {isEditing ? (
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setEditedTask(task);
                      setIsEditing(false);
                    }}
                    className="flex-1 px-4 py-3 border rounded-xl text-slate-700 hover:bg-slate-100 transition-colors font-medium"
                  >
                    {t('cancel')}
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex-1 px-4 py-3 bg-[#007cf0] text-white rounded-xl hover:bg-[#0066cc] transition-colors font-medium flex items-center justify-center gap-2"
                  >
                    {saving ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Save size={18} />
                        {t('saveChanges')}
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  {showDeleteConfirm ? (
                    <>
                      <button
                        onClick={() => setShowDeleteConfirm(false)}
                        className="flex-1 px-4 py-3 border rounded-xl text-slate-700 hover:bg-slate-100 transition-colors font-medium"
                      >
                        {t('cancel')}
                      </button>
                      <button
                        onClick={handleDelete}
                        className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium flex items-center justify-center gap-2"
                      >
                        <Trash2 size={18} />
                        {t('confirmDelete')}
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="w-full px-4 py-3 border border-red-200 text-red-600 rounded-xl hover:bg-red-50 transition-colors font-medium flex items-center justify-center gap-2"
                    >
                      <Trash2 size={18} />
                      {t('deleteTask')}
                    </button>
                  )}
                </div>
              )}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
